import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorLine } from '../../api/client';
import type { DemoCatalog, DemoFixture } from '../../api/types';
import { Modal } from '../../components/ui/modal';

export function DemoTable({
  fixtures,
  onInstall,
  onRebuild,
}: {
  fixtures: DemoFixture[];
  onInstall: (f: DemoFixture) => void;
  onRebuild: (f: DemoFixture) => void;
}) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Organization</th>
          <th>Version</th>
          <th>Status</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {fixtures.map((f) => (
          <tr key={f.scenario}>
            <td>{f.name}</td>
            <td className="quiet-cell">{f.version}</td>
            <td className="quiet-cell">
              {f.status === 'available'
                ? 'Not installed'
                : f.status === 'installed'
                  ? 'Installed'
                  : 'Different installation — review required'}
            </td>
            <td className="actions">
              {f.status === 'available' ? (
                <button className="btn act tiny" onClick={() => onInstall(f)}>
                  Install
                </button>
              ) : null}
              {f.status === 'installed' && f.org_id ? (
                <>
                  <Link className="btn act tiny" to={`/organizations/${f.org_id}`}>
                    Manage access
                  </Link>
                  <a className="btn act tiny" href={f.review_url!} target="_blank" rel="noreferrer">
                    Open demo
                  </a>
                  <button className="btn act tiny" onClick={() => onInstall(f)}>
                    Verify installation
                  </button>
                </>
              ) : null}
              {f.installations.length ? (
                <button className="btn act tiny" onClick={() => onRebuild(f)}>
                  Wipe and recreate
                </button>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Demos() {
  const [catalog, setCatalog] = useState<DemoCatalog | null>(null);
  const [selected, setSelected] = useState<DemoFixture | null>(null);
  const [rebuildId, setRebuildId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = async () => {
    setCatalog(await api.demoFixtures());
  };
  useEffect(() => {
    refresh().catch((e) => setError(errorLine(e)));
  }, []);
  const install = async () => {
    if (!selected || !catalog || busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const body = { manifest_sha256: selected.manifest_sha256, environment: catalog.environment };
      const result = rebuildId
        ? await api.rebuildDemo(selected.scenario, {
            ...body,
            request_id: rebuildId,
            targets: selected.installations.map(({ org_id, installation_id, manifest_sha256 }) => ({
              org_id,
              installation_id,
              manifest_sha256,
            })),
          })
        : await api.installDemo(selected.scenario, body);
      setCatalog(
        (current) =>
          current && {
            ...current,
            fixtures: current.fixtures.map((f) => (f.scenario === result.scenario ? result : f)),
          },
      );
      setSelected(null);
      setNotice(
        rebuildId
          ? `${result.name} was recreated. Existing memberships were preserved.`
          : `${result.name} is ready. Your existing account has access.`,
      );
    } catch (e) {
      setError(errorLine(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="head">
        <div className="titles">
          <div className="title">Demo organizations</div>
          <div className="subline">
            {catalog ? `${catalog.environment} · ${catalog.app_url}` : 'Loading.'}
          </div>
        </div>
        <Link className="btn act" to="/">
          Organizations
        </Link>
      </div>
      <p>
        Install the reviewed fictional scenarios once. Each organization is read-only and uses
        stored evidence. No research is started.
      </p>
      {notice ? <p role="status">{notice}</p> : null}
      {error && !selected ? (
        <div className="flag row" role="alert">
          {error}
        </div>
      ) : null}
      {catalog ? (
        <DemoTable
          fixtures={catalog.fixtures}
          onInstall={(f) => {
            setSelected(f);
            setRebuildId(null);
            setError(null);
          }}
          onRebuild={(f) => {
            setSelected(f);
            setRebuildId(crypto.randomUUID());
            setError(null);
          }}
        />
      ) : null}
      {selected && catalog ? (
        <Modal
          label="Provision demo"
          onClose={() => {
            if (!busy) setSelected(null);
          }}
        >
          <div className="modal-head">
            {rebuildId
              ? 'Wipe and recreate'
              : selected.status === 'installed'
                ? 'Verify installation'
                : 'Install demo'}
          </div>
          <p>
            {selected.name} · version {selected.version}
          </p>
          <p>
            Destination: <strong>{catalog.environment}</strong> — {catalog.app_url}
          </p>
          {rebuildId ? (
            <>
              <p>
                Permanently delete the {selected.installations.length} existing installation(s)
                below, including their demo data and audit history, and recreate version{' '}
                {selected.version}. Existing members keep access. Other organizations are
                unaffected. If rebuilding fails, the existing data remains.
              </p>
              <ul>
                {selected.installations.map((i) => (
                  <li key={i.org_id}>
                    {selected.name} — version {i.version}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p>
              {selected.status === 'installed'
                ? 'Check the stored evidence and restore any missing documents. Existing data and history are preserved.'
                : 'Create this demo organization and grant your existing account access. Existing organizations are preserved.'}
            </p>
          )}
          {error ? (
            <div className="flag row" role="alert">
              {error}
            </div>
          ) : null}
          <div className="modal-actions">
            <button className="btn act" disabled={busy} onClick={() => setSelected(null)}>
              Cancel
            </button>
            <button className="btn act" disabled={busy} onClick={install}>
              {busy
                ? 'Working…'
                : rebuildId
                  ? 'Wipe and recreate'
                  : selected.status === 'installed'
                    ? 'Verify'
                    : 'Install demo'}
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
